import React from 'react';
import ZoobbeSelect from '../Global/ZoobbeSelect';

const TriggerSelector = ({ triggers, selectedTrigger, onSelect, onConfigChange, triggerConfig, lists, members }) => {
  if (!triggers) return null;

  const handleTriggerSelect = (triggerType) => {
    const trigger = triggers.find(t => t.type === triggerType);
    onSelect(trigger);
  };

  const renderConfigField = (field) => {
    const value = triggerConfig[field.key] ?? field.default ?? '';

    switch (field.type) {
      case 'list_select':
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <ZoobbeSelect
              options={[
                { label: 'Select a list...', value: '' },
                ...(lists?.map(list => ({ label: list.title, value: list._id })) || [])
              ]}
              value={value}
              onSelect={(opt) => onConfigChange(field.key, opt.value)}
            />
          </div>
        );

      case 'number':
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <input
              type="number"
              value={value}
              placeholder={field.default?.toString()}
              onChange={(e) => onConfigChange(field.key, parseInt(e.target.value) || '')}
            />
          </div>
        );

      case 'priority_select':
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <ZoobbeSelect
              options={[
                { label: 'Any priority', value: '' },
                { label: 'Urgent', value: 'Urgent' },
                { label: 'High', value: 'High' },
                { label: 'Normal', value: 'Normal' },
                { label: 'Low', value: 'Low' }
              ]}
              value={value}
              onSelect={(opt) => onConfigChange(field.key, opt.value)}
            />
          </div>
        );

      case 'label_select':
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <input
              type="text"
              value={value}
              placeholder="Label name (leave empty for any)"
              onChange={(e) => onConfigChange(field.key, e.target.value)}
            />
          </div>
        );

      case 'schedule_picker':
        return <SchedulePicker key={field.key} config={triggerConfig} onChange={onConfigChange} />;

      default:
        return (
          <div className="config-field" key={field.key}>
            <label>{field.label}{field.required && ' *'}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => onConfigChange(field.key, e.target.value)}
            />
          </div>
        );
    }
  };

  const selectedTriggerDef = triggers.find(t => t.type === selectedTrigger?.type);

  return (
    <div className="trigger-selector">
      <div className="option-grid">
        {triggers.map(trigger => (
          <div
            key={trigger.type}
            className={`option-card ${selectedTrigger?.type === trigger.type ? 'selected' : ''}`}
            onClick={() => handleTriggerSelect(trigger.type)}
          >
            {trigger.label}
          </div>
        ))}
      </div>

      {selectedTriggerDef?.configFields?.length > 0 && (
        <div className="config-fields">
          {selectedTriggerDef.configFields.map(renderConfigField)}
        </div>
      )}
    </div>
  );
};

const FREQUENCY_OPTIONS = [
  { label: 'Every hour', value: 'hourly' },
  { label: 'Every day', value: 'daily' },
  { label: 'Every week', value: 'weekly' },
  { label: 'Every month', value: 'monthly' }
];

const DAY_OF_WEEK_OPTIONS = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' }
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: `${i.toString().padStart(2, '0')}:00`,
  value: String(i)
}));

const MINUTE_OPTIONS = [
  { label: ':00', value: '0' },
  { label: ':15', value: '15' },
  { label: ':30', value: '30' },
  { label: ':45', value: '45' }
];

const SchedulePicker = ({ config, onChange }) => {
  const frequency = config.frequency || 'daily';
  const dayOfWeek = config.dayOfWeek || '1';
  const dayOfMonth = config.dayOfMonth || '1';
  const hour = config.hour || '9';
  const minute = config.minute || '0';

  return (
    <div className="schedule-picker">
      <div className="schedule-row">
        <label>Frequency:</label>
        <ZoobbeSelect
          options={FREQUENCY_OPTIONS}
          value={frequency}
          onSelect={(opt) => onChange('frequency', opt.value)}
        />
      </div>

      {frequency === 'weekly' && (
        <div className="schedule-row">
          <label>Day:</label>
          <ZoobbeSelect
            options={DAY_OF_WEEK_OPTIONS}
            value={dayOfWeek}
            onSelect={(opt) => onChange('dayOfWeek', opt.value)}
          />
        </div>
      )}

      {frequency === 'monthly' && (
        <div className="schedule-row">
          <label>Day of month:</label>
          <input
            type="number"
            min="1"
            max="28"
            value={dayOfMonth}
            onChange={(e) => onChange('dayOfMonth', e.target.value)}
          />
        </div>
      )}

      {frequency !== 'hourly' && (
        <div className="schedule-row">
          <label>Time:</label>
          <ZoobbeSelect
            options={HOUR_OPTIONS}
            value={hour}
            onSelect={(opt) => onChange('hour', opt.value)}
          />
          <ZoobbeSelect
            options={MINUTE_OPTIONS}
            value={minute}
            onSelect={(opt) => onChange('minute', opt.value)}
          />
        </div>
      )}
    </div>
  );
};

// Helper to convert schedule config to cron expression
export const scheduleConfigToCron = (config) => {
  const frequency = config.frequency || 'daily';
  const minute = config.minute || '0';
  const hour = config.hour || '9';
  const dayOfMonth = config.dayOfMonth || '1';
  const dayOfWeek = config.dayOfWeek || '1';

  switch (frequency) {
    case 'hourly': return `0 * * * *`;
    case 'daily': return `${minute} ${hour} * * *`;
    case 'weekly': return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly': return `${minute} ${hour} ${dayOfMonth} * *`;
    default: return `${minute} ${hour} * * *`;
  }
};

export default TriggerSelector;
